import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { Select, message, Skeleton, Tooltip, Button, Dropdown } from 'antd';
import { useNavigate, useLocation } from 'react-router-dom';
import { LoadingOutlined } from '@ant-design/icons';

import { useCompany } from '../../../contexts/CompanyContext';
import { useRoute } from '../../../contexts/RouteContext';
import { useLoading } from '../../../contexts/LoadingContext';
import LoansService from '../../../services/loans.service';
import BorrowingBaseService from '../../../services/borrowing-base.service';
import FloorplansService from '../../../services/floorplans.service';
import ImportsService from '../../../services/imports.service';
import { useAuth } from '../../../contexts';
import { DEALER_ROLE_IDS } from '../../../enums/UserRoleId.enum';
import { Company } from '../../../types/Company.type';
import { BreadcrumbDropdown } from '../Breadcrumb';
import './CompanySelector.scss';

interface SummaryItem {
  RecId: number;
}

interface CompanySelectorProps {
  collapsed?: boolean;
  /**
   * Visual variant.
   *   - "select" (default) — full Ant Select used in sidebars/forms.
   *   - "breadcrumb"      — transparent pill + chevron meant to sit
   *                         inside a breadcrumb trail.
   */
  variant?: 'select' | 'breadcrumb';
}

const BORROWING_BASE = 'borrowing-base';
const DEALERS = 'dealers';
const FLOORPLANS = 'floorplans';
const IMPORTS = 'imports';
const LOANS = 'loans';
const companyPaths = [BORROWING_BASE, DEALERS, FLOORPLANS, IMPORTS, LOANS];

const CompanySelector: React.FC<CompanySelectorProps> = ({ collapsed, variant = 'select' }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { company_id, companies, setCompany, loading: companiesLoading } = useCompany();
  const { updateRouteParams } = useRoute();
  const { setLoading } = useLoading();
  const { user } = useAuth();
  const [availableCompanies, setAvailableCompanies] = useState<Company[]>([]);
  const [isChangingCompany, setIsChangingCompany] = useState(false);
  const [selectorLoading, setSelectorLoading] = useState(false);

  const isDetailPage = useMemo((): boolean => {
    const pathname = location.pathname;
    const detailPathPatterns = [
      /^\/floorplans\/\d+\/pending-funding\/detail\/\d+$/,
      /^\/floorplans\/\d+\/active-vehicles\/detail\/\d+$/,
      /^\/floorplans\/\d+\/paid-off-vehicles\/detail\/\d+$/,
      /^\/floorplans\/\d+\/funding-log\/detail\/\d+$/,
      /^\/loans\/\d+\/active-loans\/detail\/\d+$/,
      /^\/loans\/\d+\/active-loans\/detail\/\d+\/repo-swap$/,
      /^\/loans\/\d+\/funding-log\/detail\/\d+$/,
      /^\/loans\/\d+\/matchup-exceptions\/detail\/.+$/,
      /^\/loans\/\d+\/paid-off-loans\/detail\/\d+$/,
      /^\/dealers\/\d+\/contacts\/\d+\/edit$/,
      /^\/dealers\/\d+\/portal-users\/\d+\/edit$/,
    ];
    return detailPathPatterns.some(pattern => pattern.test(pathname));
  }, [location.pathname]);

  const selectedCompany = useMemo(() => {
    return (
      availableCompanies.find(c => c.RecId === company_id) ||
      companies.find(c => c.RecId === company_id)
    );
  }, [availableCompanies, companies, company_id]);

  const companyInitial = useMemo(() => {
    if (!selectedCompany?.CompanyName) return '?';
    return selectedCompany.CompanyName.charAt(0).toUpperCase();
  }, [selectedCompany]);

  const updateCompanyIdInPath = (path: string, newId: string | number) => {
    for (const segment of companyPaths) {
      const regex = new RegExp(`/${segment}/\\d+`);
      if (regex.test(path)) {
        return path.replace(regex, `/${segment}/${newId}`);
      }
    }
    return path;
  };

  const handleCompanyChange = useCallback(
    async (value: number) => {
      const selectedCompany = companies.find(c => c.RecId === value);
      if (selectedCompany) {
        setIsChangingCompany(true);
        setLoading(true, 'Switching Company');

        try {
          setCompany(selectedCompany);
          const newPath = updateCompanyIdInPath(location.pathname, value);
          navigate(newPath);
          updateRouteParams(value.toString());
        } catch (error) {
          console.error('Error changing company:', error);
          message.error('Failed to switch company. Please try again.');
        } finally {
          setIsChangingCompany(false);
          setLoading(false);
        }
      }
    },
    [companies, navigate, location.pathname, setCompany, updateRouteParams, setLoading]
  );

  const setCompanies = useCallback(
    (key: string, _items: SummaryItem[], _companies: Company[]) => {
      let summaryDataItems = _items;
      if (user && DEALER_ROLE_IDS.has(Number(user.role_id))) {
        summaryDataItems = summaryDataItems.filter((item: any) => item.RecId === user?.company_id);
      }
      const availableIds = summaryDataItems.map((item: any) => item.RecId);
      const filteredCompanies = _companies.filter(item => availableIds.includes(item.RecId));

      // Sort by ShortName (trimmed for consistent sorting)
      const sortedCompanies = filteredCompanies.sort((a, b) => {
        const nameA = (a.ShortName || '').trim();
        const nameB = (b.ShortName || '').trim();
        return nameA.localeCompare(nameB, undefined, { sensitivity: 'base' });
      });

      setAvailableCompanies(sortedCompanies);
      localStorage.setItem(key, JSON.stringify(sortedCompanies));
      setSelectorLoading(false);
    },
    [user]
  );

  const setBorrowingBaseCompanies = useCallback(
    (_companies: Company[]) => {
      const key = `${BORROWING_BASE}-companies`;
      setSelectorLoading(true);
      const cachedCompanies = localStorage.getItem(key);
      if (cachedCompanies) {
        setAvailableCompanies(JSON.parse(cachedCompanies));
      }

      // Use lightweight endpoint instead of full summary
      BorrowingBaseService.getCompaniesWithBorrowingBase()
        .then(response => {
          setCompanies(key, response.data, _companies);
        })
        .catch(error => {
          console.error('Error fetching borrowing base companies:', error);
          message.error('Failed to load borrowing base companies. Please try again.');
          setSelectorLoading(false);
        });
    },
    [setCompanies]
  );

  const setDealerCompanies = useCallback((_companies: Company[]) => {
    setSelectorLoading(true);

    // Sort by ShortName (trimmed for consistent sorting)
    const sortedCompanies = _companies.sort((a, b) => {
      const nameA = (a.ShortName || '').trim();
      const nameB = (b.ShortName || '').trim();
      return nameA.localeCompare(nameB, undefined, { sensitivity: 'base' });
    });

    setAvailableCompanies(sortedCompanies);
    setSelectorLoading(false);
  }, []);

  const setFloorPlanCompanies = useCallback(
    (_companies: Company[]) => {
      const key = `${FLOORPLANS}-companies`;
      setSelectorLoading(true);
      const cachedCompanies = localStorage.getItem(key);
      if (cachedCompanies) {
        setAvailableCompanies(JSON.parse(cachedCompanies));
      }

      FloorplansService.getFloorplansDashboard()
        .then(response => {
          setCompanies(key, response.data, _companies);
        })
        .catch(error => {
          console.error('Error fetching floorplan companies:', error);
          message.error('Failed to load floorplan companies. Please try again.');
          setSelectorLoading(false);
        });
    },
    [setCompanies]
  );

  const setImportCompanies = useCallback(
    (_companies: Company[]) => {
      const key = `${IMPORTS}-companies`;
      setSelectorLoading(true);
      const cachedCompanies = localStorage.getItem(key);
      if (cachedCompanies) {
        setAvailableCompanies(JSON.parse(cachedCompanies));
      }

      ImportsService.importsSummary()
        .then(response => {
          setCompanies(key, response.data, _companies);
        })
        .catch(error => {
          console.error('Error fetching import companies:', error);
          message.error('Failed to load import companies. Please try again.');
          setSelectorLoading(false);
        });
    },
    [setCompanies]
  );

  const setLoanCompanies = useCallback(
    (_companies: Company[]) => {
      const key = `${LOANS}-companies`;
      setSelectorLoading(true);
      const cachedCompanies = localStorage.getItem(key);
      if (cachedCompanies) {
        setAvailableCompanies(JSON.parse(cachedCompanies));
      }

      // Use lightweight endpoint instead of full summary
      LoansService.getCompaniesWithLoans()
        .then(response => {
          setCompanies(key, response.data, _companies);
        })
        .catch(error => {
          console.error('Error fetching loan companies:', error);
          message.error('Failed to load loan companies. Please try again.');
          setSelectorLoading(false);
        });
    },
    [setCompanies]
  );

  useEffect(() => {
    if (!companies.length) return;

    if (user && DEALER_ROLE_IDS.has(Number(user.role_id))) {
      setAvailableCompanies([]);
      return;
    }

    const companyPathHandlers: Record<string, (companies: Company[]) => void> = {
      [BORROWING_BASE]: setBorrowingBaseCompanies,
      [DEALERS]: setDealerCompanies,
      [FLOORPLANS]: setFloorPlanCompanies,
      [IMPORTS]: setImportCompanies,
      [LOANS]: setLoanCompanies,
    };

    const matchedSegment = companyPaths.find(segment => {
      const regex = new RegExp(`/${segment}/\\d+`);
      return regex.test(location.pathname);
    });

    if (matchedSegment && companyPathHandlers[matchedSegment]) {
      companyPathHandlers[matchedSegment](companies);
    }
  }, [
    companies,
    location.pathname,
    user,
    setBorrowingBaseCompanies,
    setDealerCompanies,
    setFloorPlanCompanies,
    setImportCompanies,
    setLoanCompanies,
  ]);

  if (companiesLoading || selectorLoading) {
    if (variant === 'breadcrumb') {
      return (
        <span className="breadcrumb-dropdown-trigger is-disabled" aria-busy="true">
          <span className="breadcrumb-dropdown-label">
            <LoadingOutlined style={{ fontSize: 12, marginRight: 4 }} /> Loading…
          </span>
        </span>
      );
    }
    if (collapsed) {
      return (
        <div style={{ position: 'relative' }}>
          <div
            style={{
              width: 32,
              height: 32,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: '#f5f5f5',
              border: '1px solid #d9d9d9',
              color: '#999',
            }}
          >
            <LoadingOutlined style={{ fontSize: 14 }} />
          </div>
        </div>
      );
    }

    return (
      <div style={{ width: '100%', maxWidth: 240 }}>
        <Skeleton.Input active style={{ width: '100%', height: 32 }} block />
      </div>
    );
  }

  if (!availableCompanies.length && !companiesLoading) {
    if (variant === 'breadcrumb') {
      return (
        <span className="breadcrumb-dropdown-trigger is-disabled">
          <span className="breadcrumb-dropdown-label">
            {selectedCompany?.ShortName || selectedCompany?.CompanyName || 'No companies'}
          </span>
        </span>
      );
    }
    return (
      <div
        style={{
          width: collapsed ? 40 : '100%',
          maxWidth: collapsed ? 40 : 240,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <span style={{ fontSize: collapsed ? 10 : 12, color: '#999' }}>
          {collapsed ? 'N/A' : 'No companies available'}
        </span>
      </div>
    );
  }

  if (variant === 'breadcrumb') {
    const dropdownItems = availableCompanies.map((company: Company) => ({
      key: company.RecId,
      label: company.ShortName || company.CompanyName,
      onClick: () => handleCompanyChange(company.RecId),
      disabled: isDetailPage,
    }));

    return (
      <BreadcrumbDropdown
        label={selectedCompany?.ShortName || selectedCompany?.CompanyName || 'Select Company'}
        items={dropdownItems}
        selectedKey={company_id}
        disabled={isChangingCompany || isDetailPage}
        title={
          isDetailPage ? 'Company selection disabled on detail pages' : selectedCompany?.CompanyName
        }
      />
    );
  }

  if (collapsed) {
    const dropdownItems = availableCompanies.map((company: Company) => {
      return {
        key: company.RecId,
        label: company.ShortName || company.CompanyName,
        onClick: () => handleCompanyChange(company.RecId),
        disabled: isDetailPage,
      };
    });

    return (
      <div style={{ position: 'relative' }}>
        {availableCompanies.length > 0 ? (
          <Dropdown
            menu={{ items: dropdownItems }}
            placement="bottom"
            trigger={['click']}
            disabled={isDetailPage}
          >
            <Tooltip
              title={
                selectedCompany?.CompanyName ||
                (isDetailPage ? 'Company selection disabled on detail pages' : 'Select Company')
              }
              placement="right"
            >
              <Button
                type="text"
                size="small"
                disabled={isDetailPage}
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 14,
                  fontWeight: 'bold',
                  backgroundColor: isDetailPage ? '#f5f5f5' : '#233867',
                  border: `1px solid ${isDetailPage ? '#d9d9d9' : '#233867'}`,
                  color: isDetailPage ? '#999' : '#fff',
                  cursor: isDetailPage ? 'not-allowed' : 'pointer',
                  padding: 0,
                  transition: 'all 0.3s ease',
                }}
                onMouseEnter={e => {
                  if (!isDetailPage) {
                    e.currentTarget.style.backgroundColor = '#3574ea';
                    e.currentTarget.style.borderColor = '#3574ea';
                  }
                }}
                onMouseLeave={e => {
                  if (!isDetailPage) {
                    e.currentTarget.style.backgroundColor = '#233867';
                    e.currentTarget.style.borderColor = '#233867';
                  }
                }}
              >
                {companyInitial}
              </Button>
            </Tooltip>
          </Dropdown>
        ) : (
          <Tooltip title="No companies available" placement="right">
            <Button
              type="text"
              size="small"
              style={{
                width: 32,
                height: 32,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 14,
                fontWeight: 'bold',
                backgroundColor: '#f5f5f5',
                border: '1px solid #d9d9d9',
                color: '#999',
                cursor: 'not-allowed',
                padding: 0,
              }}
              disabled
            >
              ?
            </Button>
          </Tooltip>
        )}
      </div>
    );
  }

  return (
    <div style={{ position: 'relative' }}>
      <Select
        style={{ width: collapsed ? 40 : '100%', maxWidth: 240 }}
        value={company_id}
        onChange={handleCompanyChange}
        placeholder="Select a company"
        showSearch
        disabled={isChangingCompany || isDetailPage}
        optionFilterProp="children"
        loading={isChangingCompany}
        filterOption={(input, option) => {
          const label = String(option?.label || option?.children || '');
          return label.toLowerCase().includes(input.toLowerCase());
        }}
        title={isDetailPage ? 'Company selection disabled on detail pages' : undefined}
      >
        {availableCompanies.map((company: Company) => (
          <Select.Option key={company.RecId} value={company.RecId}>
            {company.ShortName || company.CompanyName}
          </Select.Option>
        ))}
      </Select>
    </div>
  );
};

export default CompanySelector;
